import { useReactiveVar } from '@apollo/client';
import ClearIcon from '@mui/icons-material/Clear';
import { Autocomplete, Chip, IconButton, InputLabel, Switch, TextField, Typography } from '@mui/material';
import { DesktopTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LockForm } from 'components/Drawer/types';
import { Value } from 'components/Multiselect/Multiselect';
import dayjs from 'dayjs';
import { DoorBatteryWarningLevel } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { upperFirst } from 'lodash';
import React from 'react';
import { Control, Controller, UseFormRegister } from 'react-hook-form';
import { authVar } from 'state/vars';
import styled from 'styled-components';
import AutocompletePaper from 'ui/atoms/AutocompletePaper/AutocompletePaper';
import Icon from 'ui/atoms/Icon/Icon';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';
import BatteryStatusAlert from 'ui/molecules/BatteryStatusBox/BatteryStatusAlert';
import OfficeModeTooltipInfo from 'ui/organisms/OfficeModeTooltipInfo/OfficeModeTooltipInfo';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

const IconWrapper = styled.div`
  cursor: pointer;
  align-items: center;
`;

export interface Props {
  register: UseFormRegister<LockForm>;
  control: Control<LockForm, object>;
  defaultValues: LockForm;
  nameInputValue: string;
  handleNameInputOnChange: (value: string) => void;
  isOfficeModeAvailable: boolean;
  switchIsOfficeModeEnabled?: boolean;
  handleSwitchIsOfficeModeEnabledOnChange?: () => void;
  inputOfficeModeFromValue?: dayjs.Dayjs | null;
  handleInputOfficeModeFromValueOnChange?: (value: dayjs.Dayjs | null) => void;
  inputOfficeModeToValue?: dayjs.Dayjs | null;
  handleInputOfficeModeToValueOnChange?: (value: dayjs.Dayjs | null) => void;
  showValidation: boolean;
  nameInputValidation: string | undefined;
  officeModeTimeInputValidation: string | undefined;
  batteryStatus: DoorBatteryWarningLevel;
  handleGroupsOnChange: (event: unknown, value: Value[]) => void;
  handleGroupsMultiselectClear: () => void;
  groupsOptions: Value[];
  groupsMultiselect: Value[];
}

const LockWithGroups: React.FC<React.PropsWithChildren<Props>> = ({
  register,
  control,
  defaultValues,
  nameInputValue,
  handleNameInputOnChange,
  isOfficeModeAvailable,
  switchIsOfficeModeEnabled,
  handleSwitchIsOfficeModeEnabledOnChange,
  inputOfficeModeFromValue,
  handleInputOfficeModeFromValueOnChange,
  inputOfficeModeToValue,
  handleInputOfficeModeToValueOnChange,
  showValidation,
  nameInputValidation,
  officeModeTimeInputValidation,
  batteryStatus,
  handleGroupsOnChange,
  handleGroupsMultiselectClear,
  groupsOptions,
  groupsMultiselect
}) => {
  const { translation, getLanguage, getLocaleForDayjs } = useTranslation();
  const { user } = useReactiveVar(authVar);

  return (
    <ComponentWrapper flexDirection="column" gap="1.5rem" margin="0.5rem 0 0 0">
      <div>
        <InputLabel variant="outlined" shrink>
          {translation.serial_number}
        </InputLabel>
        <Typography variant="body1" id="drawer-serial-number" style={{ marginLeft: '1rem' }}>
          {defaultValues.hardwareIdentifier}
        </Typography>
      </div>
      <TextField
        {...register('name')}
        onChange={(event) => handleNameInputOnChange(event.target.value)}
        variant="outlined"
        InputLabelProps={{ shrink: nameInputValue.length > 0 }}
        size="medium"
        label={translation.name}
        value={nameInputValue}
        id="drawer-input-name"
        error={showValidation && nameInputValidation !== undefined}
        helperText={showValidation ? nameInputValidation : undefined}
      />
      <Controller
        control={control}
        name="groupIds"
        render={() => (
          <Autocomplete
            {...register('groupIds')}
            multiple
            options={groupsOptions.filter((option) => !groupsMultiselect.some((group) => group.value === option.value))}
            value={groupsMultiselect}
            onChange={handleGroupsOnChange}
            renderTags={(value, getTagProps) =>
              value.map((option) => (
                <Chip size="small" label={option.label} {...getTagProps({ index: Math.random() })} />
              ))
            }
            clearIcon={
              <IconButton size="small" onClick={handleGroupsMultiselectClear}>
                <ClearIcon />
              </IconButton>
            }
            PaperComponent={AutocompletePaper}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={`${translation.lock_group_s}`}
                placeholder={groupsMultiselect.length === 0 ? translation.select_group : undefined}
                size="medium"
                InputLabelProps={{ shrink: !!params.inputProps.value || groupsMultiselect.length > 0 }}
                InputProps={{
                  ...params.InputProps,
                  style: { paddingRight: '9px' }
                }}
                id="drawer-input-lock-groups"
              />
            )}
            id="drawer-input-lock-groups"
          />
        )}
      />
      {isOfficeModeAvailable && handleInputOfficeModeFromValueOnChange && handleInputOfficeModeToValueOnChange && (
        <>
          <Controller
            control={control}
            name="isOfficeModeEnabled"
            render={() => (
              <ComponentWrapper flexDirection="row" gap="1rem" alignItems="center" justifyContent="space-between">
                <ComponentWrapper alignItems="center">
                  <Switch
                    {...register('isOfficeModeEnabled')}
                    color="primary"
                    id="drawer-input-office-mode-switch"
                    checked={switchIsOfficeModeEnabled}
                    onClick={handleSwitchIsOfficeModeEnabledOnChange}
                  />
                  <Typography variant="body1">{translation.enable_office_mode}</Typography>
                </ComponentWrapper>
                <Tooltip elementOnHover={<OfficeModeTooltipInfo />} id="drawer-tooltip">
                  <IconWrapper>
                    <Icon name="Info" id="drawer-i-icon" />
                  </IconWrapper>
                </Tooltip>
              </ComponentWrapper>
            )}
          />
          <ComponentWrapper alignItems="center" justifyContent="space-between" gap="0.5rem">
            <Controller
              control={control}
              name="officeModeFromValue"
              render={() => (
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getLocaleForDayjs(getLanguage).name}>
                  <DesktopTimePicker
                    ampm={false}
                    label={upperFirst(translation.start_time)}
                    value={inputOfficeModeFromValue}
                    disabled={!switchIsOfficeModeEnabled}
                    onChange={(event) => (event ? handleInputOfficeModeFromValueOnChange(event) : undefined)}
                    maxTime={inputOfficeModeToValue ? inputOfficeModeToValue : undefined}
                    slotProps={{
                      textField: {
                        helperText:
                          showValidation && !!officeModeTimeInputValidation ? officeModeTimeInputValidation : undefined,
                        id: 'drawer-input-from'
                      }
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <Controller
              control={control}
              name="officeModeToValue"
              render={() => (
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getLocaleForDayjs(getLanguage).name}>
                  <DesktopTimePicker
                    ampm={false}
                    label={upperFirst(translation.end_time)}
                    value={inputOfficeModeToValue}
                    disabled={!switchIsOfficeModeEnabled}
                    onChange={(event) => (event ? handleInputOfficeModeToValueOnChange(event) : undefined)}
                    minTime={inputOfficeModeFromValue ? inputOfficeModeFromValue : undefined}
                    slotProps={{
                      textField: {
                        helperText:
                          showValidation && !!officeModeTimeInputValidation ? officeModeTimeInputValidation : undefined,
                        id: 'drawer-input-to'
                      }
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </ComponentWrapper>
        </>
      )}
      <ComponentWrapper flexDirection="column">
        <InputLabel variant="outlined" shrink>
          {translation.lock_warnings}
        </InputLabel>
        <BatteryStatusAlert batteryStatus={batteryStatus} />
      </ComponentWrapper>
    </ComponentWrapper>
  );
};

export default LockWithGroups;
